import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../../firebase/config';
import { ShieldCheck, Loader, CheckCircle2, AlertCircle, RefreshCw } from 'lucide-react';

/**
 * 기존 claimedBy 사용자 일괄 마이그레이션 (1회용 임시 어드민 버튼)
 *
 * 동작:
 *   1. 현재 hospitals 컬렉션에서 claimedBy != null AND matchStatus == null 검색
 *   2. 결과 미리보기
 *   3. 버튼 클릭 → migrateExistingClaims Cloud Function 호출
 *   4. 결과 표시 (verified_legacy 부여 완료)
 *
 * 권한: admin 토큰 (`request.auth.token.admin == true`) 필요
 */
function AdminClaimMigration() {
    const [pending, setPending] = useState([]);
    const [scanning, setScanning] = useState(true);
    const [migrating, setMigrating] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => { scan(); }, []);

    const scan = async () => {
        setScanning(true);
        setError(null);
        try {
            // claimedBy != null 검색 (Firestore != 쿼리는 인덱스 필요할 수 있음)
            const snap = await getDocs(query(collection(db, 'hospitals'), where('claimedBy', '!=', null)));
            const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            // 클라이언트에서 matchStatus 없는 것만 필터링
            const list = all.filter(h => !h.matchStatus);
            setPending(list);
        } catch (e) {
            setError('스캔 실패: ' + e.message);
        } finally {
            setScanning(false);
        }
    };

    const handleMigrate = async () => {
        if (!window.confirm(`${pending.length}건을 verified_legacy로 마이그레이션하시겠습니까?\n(되돌릴 수 없습니다)`)) return;
        setMigrating(true);
        setError(null);
        try {
            const fns = getFunctions(undefined, 'asia-northeast3');
            const fn = httpsCallable(fns, 'migrateExistingClaims');
            const res = await fn({});
            setResult(res.data);
            // 마이그레이션 후 다시 스캔
            await scan();
        } catch (e) {
            setError(e.message || '마이그레이션 실패');
        } finally {
            setMigrating(false);
        }
    };

    return (
        <div style={S.page}>
            <div style={S.header}>
                <h1 style={S.title}>
                    <ShieldCheck size={22} style={{ verticalAlign: 'middle', marginRight: 8 }} />
                    Legacy claimedBy 마이그레이션 (1회용)
                </h1>
                <p style={S.subtitle}>
                    기존에 등록된 치과(claimedBy != null)에 매칭 메타데이터(matchStatus 등)를 부여합니다.
                    <br />
                    신규 매칭 시스템(Phase A~D)과 호환되도록 합니다.
                </p>
            </div>

            {/* 안내 박스 */}
            <div style={S.infoBox}>
                <strong>처리 내용</strong>
                <p style={{ fontSize: 12, color: '#075985', margin: '4px 0 0', lineHeight: 1.7 }}>
                    각 doc에 다음 필드 자동 추가:<br />
                    • <code>matchStatus: "verified_legacy"</code><br />
                    • <code>matchScore: 100</code><br />
                    • <code>verifiedBy: "system_legacy"</code><br />
                    • <code>legacyMigration: true</code><br />
                    • <code>matchedAt: 기존 claimedAt 또는 현재 시각</code>
                </p>
            </div>

            {/* 스캔 결과 */}
            <div style={S.section}>
                <div style={S.sectionHeader}>
                    <h3 style={S.sectionTitle}>마이그레이션 대상 ({scanning ? '...' : pending.length}건)</h3>
                    <button onClick={scan} disabled={scanning} style={S.refreshBtn}>
                        {scanning ? <Loader size={12} className="spin" /> : <RefreshCw size={12} />}
                        새로고침
                    </button>
                </div>

                {scanning ? (
                    <div style={S.empty}>
                        <Loader size={20} /> 스캔 중...
                    </div>
                ) : pending.length === 0 ? (
                    <div style={{ ...S.empty, color: '#10b981' }}>
                        <CheckCircle2 size={20} /> 마이그레이션 대상이 없습니다 (이미 처리됨)
                    </div>
                ) : (
                    <div style={S.list}>
                        {pending.map(h => (
                            <div key={h.id} style={S.row}>
                                <div style={{ flex: 1 }}>
                                    <strong>{h.name || '(이름 없음)'}</strong>
                                    <div style={S.meta}>
                                        <span>📍 {h.address || '주소 없음'}</span>
                                    </div>
                                    <div style={S.meta}>
                                        <span>👤 claimedBy: {h.claimedBy || '-'}</span>
                                    </div>
                                </div>
                                <span style={S.statusBadge}>대기</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {error && (
                <div style={S.error}>
                    <AlertCircle size={14} /> {error}
                </div>
            )}

            {result && (
                <div style={S.success}>
                    <CheckCircle2 size={14} />
                    <strong>완료:</strong> 총 {result.total}건 처리됨
                    {Array.isArray(result.results) && (
                        <details style={{ marginTop: 8, fontSize: 11 }}>
                            <summary style={{ cursor: 'pointer' }}>상세 결과 보기</summary>
                            <pre style={S.resultLog}>{JSON.stringify(result.results, null, 2)}</pre>
                        </details>
                    )}
                </div>
            )}

            {/* 실행 버튼 */}
            <div style={{ marginTop: 24, textAlign: 'center' }}>
                <button
                    onClick={handleMigrate}
                    disabled={migrating || scanning || pending.length === 0}
                    style={{
                        ...S.runBtn,
                        opacity: (migrating || scanning || pending.length === 0) ? 0.5 : 1,
                        cursor: (migrating || scanning || pending.length === 0) ? 'not-allowed' : 'pointer',
                    }}
                >
                    {migrating ? <Loader size={16} /> : <ShieldCheck size={16} />}
                    {migrating ? '마이그레이션 중...' : `${pending.length}건 일괄 마이그레이션 실행`}
                </button>
                <p style={S.warning}>
                    ⚠️ 이 작업은 1회용입니다. 실행 후 메뉴에서 제거 권장.
                </p>
            </div>
        </div>
    );
}

const S = {
    page: { maxWidth: 800, margin: '0 auto', padding: 24 },
    header: { marginBottom: 20 },
    title: { fontSize: 22, fontWeight: 900, margin: 0, color: '#0f172a' },
    subtitle: { fontSize: 13, color: '#64748b', marginTop: 6, lineHeight: 1.6 },
    infoBox: {
        padding: 14, marginBottom: 20,
        background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10,
        fontSize: 13, color: '#075985',
    },
    section: { marginBottom: 16 },
    sectionHeader: {
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10,
    },
    sectionTitle: { fontSize: 15, fontWeight: 800, margin: 0 },
    refreshBtn: {
        display: 'inline-flex', alignItems: 'center', gap: 4,
        padding: '6px 10px', background: '#fff', border: '1px solid #cbd5e1',
        borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer', color: '#64748b',
    },
    empty: {
        padding: 30, textAlign: 'center', background: '#f8fafc',
        borderRadius: 8, fontSize: 13, color: '#64748b',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
    },
    list: { display: 'flex', flexDirection: 'column', gap: 6 },
    row: {
        display: 'flex', alignItems: 'flex-start', gap: 10,
        padding: 12, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
    },
    meta: { fontSize: 11, color: '#64748b', marginTop: 2 },
    statusBadge: {
        padding: '3px 8px', background: '#fef3c7', color: '#92400e',
        borderRadius: 4, fontSize: 10, fontWeight: 800,
    },
    error: {
        padding: 12, marginTop: 16, background: '#fee2e2', color: '#991b1b',
        borderRadius: 8, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6,
    },
    success: {
        padding: 12, marginTop: 16, background: '#d1fae5', color: '#065f46',
        borderRadius: 8, fontSize: 13, display: 'flex', alignItems: 'flex-start', gap: 6, flexWrap: 'wrap',
    },
    resultLog: {
        marginTop: 6, padding: 10, background: '#fff', borderRadius: 6,
        fontSize: 10, fontFamily: 'monospace', maxHeight: 200, overflow: 'auto',
    },
    runBtn: {
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '14px 28px', background: '#0ea5e9', color: '#fff',
        border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 800,
    },
    warning: { fontSize: 11, color: '#94a3b8', marginTop: 8 },
};

export default AdminClaimMigration;
